import React, { useState, useEffect } from 'react';
import { X, CreditCard, ArrowUpRight, AlertTriangle, CheckCircle, Copy, ExternalLink, Building, User, Landmark } from 'lucide-react';

interface BankWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  usdtBalance: number;
  onWithdraw: (amount: number, bankDetails: {
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    beneficiaryName: string;
  }) => Promise<boolean>;
}

const BankWithdrawalModal: React.FC<BankWithdrawalModalProps> = ({
  isOpen,
  onClose,
  usdtBalance,
  onWithdraw
}) => {
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'confirmation' | 'success'>('form');
  const [transactionId, setTransactionId] = useState<string | null>(null);

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setBankName('');
      setAccountNumber('');
      setRoutingNumber('');
      setBeneficiaryName('');
      setError(null);
      setSuccess(null);
      setStep('form');
      setTransactionId(null);
    }
  }, [isOpen]);

  // Calculate withdrawal fee (0.5% for bank withdrawals)
  const calculateFee = () => {
    return parseFloat(amount) * 0.005;
  };

  // Calculate amount to receive after fee
  const calculateAmountToReceive = () => {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) return 0;
    
    const fee = calculateFee();
    return parsedAmount - fee;
  };

  // Handle max button click
  const handleMaxClick = () => {
    setAmount(usdtBalance.toString());
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (parsedAmount > usdtBalance) {
      setError('Insufficient USDT balance');
      return;
    }
    
    if (!bankName || !accountNumber || !routingNumber || !beneficiaryName) {
      setError('Please fill in all bank details');
      return;
    }
    
    // Move to confirmation step
    setError(null);
    setStep('confirmation');
  };

  // Handle withdrawal confirmation
  const handleConfirmWithdrawal = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const success = await onWithdraw(parseFloat(amount), {
        bankName,
        accountNumber,
        routingNumber,
        beneficiaryName
      });
      
      if (success) {
        setSuccess(`Bank withdrawal of ${amount} USDT initiated successfully`);
        setStep('success');
        // Generate a fake transaction ID
        setTransactionId(`tx_${Math.random().toString(36).substring(2, 15)}`);
      } else {
        throw new Error('Withdrawal failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during withdrawal');
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  // Copy transaction ID to clipboard
  const copyTransactionId = () => {
    if (transactionId) {
      navigator.clipboard.writeText(transactionId);
      setSuccess('Transaction ID copied to clipboard');
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="rounded-xl max-w-md w-full p-4 sm:p-6 shadow-2xl overflow-y-auto max-h-[90vh]" style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
              background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
            }}>
              <Landmark size={20} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>
              {step === 'form' ? 'Bank Withdrawal' :
               step === 'confirmation' ? 'Confirm Withdrawal' :
               'Withdrawal Initiated'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <AlertTriangle size={20} className="flex-shrink-0" style={{ color: '#ef4444' }} />
            <span className="text-sm sm:text-base" style={{ color: '#ef4444' }}>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6" style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}>
            <CheckCircle size={20} className="flex-shrink-0" style={{ color: '#22c55e' }} />
            <span className="text-sm sm:text-base" style={{ color: '#22c55e' }}>{success}</span>
          </div>
        )}

        {/* Bank Withdrawal Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-1 sm:mb-2">
                <label className="text-xs sm:text-sm" style={{ color: '#64748b' }}>Amount (USDT)</label>
                <span className="text-xs text-right" style={{ color: '#64748b' }}>
                  Available: {usdtBalance.toFixed(2)} USDT
                </span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-16 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm sm:text-base"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1e293b'
                  }}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleMaxClick}
                    className="text-xs transition-colors px-2 py-1 rounded"
                    style={{
                      color: '#3b82f6',
                      background: 'rgba(59, 130, 246, 0.1)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                  >
                    MAX
                  </button>
                  <span style={{ color: '#64748b' }}>USDT</span>
                </div>
              </div>
              <p className="text-xs mt-0.5 sm:mt-1 ml-1" style={{ color: '#94a3b8' }}>
                Minimum withdrawal: 100 USDT
              </p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm mb-1 sm:mb-2" style={{ color: '#64748b' }}>Bank Name</label>
              <div className="relative">
                <Building size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="Enter bank name"
                  className="w-full pl-10 px-3 sm:px-4 py-2 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm sm:text-base"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm mb-1 sm:mb-2" style={{ color: '#64748b' }}>Account Number</label>
              <div className="relative">
                <CreditCard size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Enter account number"
                  className="w-full pl-10 px-3 sm:px-4 py-2 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm sm:text-base"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm mb-1 sm:mb-2" style={{ color: '#64748b' }}>Routing Number / SWIFT Code</label>
              <div className="relative">
                <Landmark size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                  placeholder="Enter routing number or SWIFT code"
                  className="w-full pl-10 px-3 sm:px-4 py-2 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm sm:text-base"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm mb-1 sm:mb-2" style={{ color: '#64748b' }}>Beneficiary Name</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  value={beneficiaryName}
                  onChange={(e) => setBeneficiaryName(e.target.value)}
                  placeholder="Enter beneficiary name"
                  className="w-full pl-10 px-3 sm:px-4 py-2 sm:py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm sm:text-base"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>

            <div className="rounded-xl p-3 sm:p-4 border" style={{
              background: 'rgba(0, 0, 0, 0.02)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <div className="flex justify-between items-center mb-1 sm:mb-2">
                <span className="text-xs sm:text-sm" style={{ color: '#64748b' }}>Withdrawal Fee (0.5%)</span>
                <span className="text-xs sm:text-sm" style={{ color: '#1e293b' }}>
                  {calculateFee().toFixed(2)} USDT
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm" style={{ color: '#64748b' }}>You Will Receive</span>
                <span className="text-xs sm:text-sm font-medium" style={{ color: '#1e293b' }}>
                  {calculateAmountToReceive().toFixed(2)} USDT
                </span>
              </div>
            </div>

            <div className="rounded-xl p-3 sm:p-4 text-xs sm:text-sm flex items-start gap-2 sm:gap-3" style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#f59e0b'
            }}>
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-0.5 sm:mb-1">Important Information</p>
                <p>Bank withdrawals typically take 1-3 business days to process. Please ensure all bank details are correct to avoid delays.</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !amount || !bankName || !accountNumber || !routingNumber || !beneficiaryName}
              className="w-full py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base text-white"
              style={{
                background: loading || !amount || !bankName || !accountNumber || !routingNumber || !beneficiaryName
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                cursor: loading || !amount || !bankName || !accountNumber || !routingNumber || !beneficiaryName ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowUpRight size={18} />
                  Withdraw to Bank
                </>
              )}
            </button>
          </form>
        )}

        {/* Confirmation Step */}
        {step === 'confirmation' && (
          <div className="space-y-6">
            <div className="rounded-xl p-3 sm:p-4 border" style={{
              background: 'rgba(0, 0, 0, 0.02)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <div className="text-center mb-3 sm:mb-4">
                <div className="text-xs sm:text-sm mb-1" style={{ color: '#64748b' }}>You are about to withdraw</div>
                <div className="text-xl sm:text-2xl font-bold" style={{ color: '#1e293b' }}>{amount} USDT</div>
                <div className="text-xs sm:text-sm" style={{ color: '#64748b' }}>
                  ≈ ${parseFloat(amount).toFixed(2)}
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Bank Name</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{bankName}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Account Number</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>••••{accountNumber.slice(-4)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Routing Number</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{routingNumber}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Beneficiary</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{beneficiaryName}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Fee (0.5%)</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{calculateFee().toFixed(2)} USDT</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>You Will Receive</span>
                  <span className="font-medium text-right" style={{ color: '#1e293b' }}>
                    {calculateAmountToReceive().toFixed(2)} USDT
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-3 sm:p-4 text-xs sm:text-sm flex items-start gap-2 sm:gap-3" style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#f59e0b'
            }}>
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-0.5 sm:mb-1">Withdrawal Warning</p>
                <p>Please verify that all bank details are correct. Withdrawals to incorrect accounts cannot be recovered.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep('form')}
                disabled={loading}
                className="flex-1 py-2 sm:py-3 rounded-xl font-medium transition-colors text-sm sm:text-base text-white"
                style={{
                  background: 'linear-gradient(135deg, #64748b, #475569)',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = 'linear-gradient(135deg, #475569, #334155)')}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = 'linear-gradient(135deg, #64748b, #475569)')}
              >
                Back
              </button>
              <button
                onClick={handleConfirmWithdrawal}
                disabled={loading}
                className="flex-1 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base text-white"
                style={{
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Confirm Withdrawal'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="space-y-6">
            <div className="text-center py-3 sm:py-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6" style={{
                background: 'rgba(34, 197, 94, 0.15)',
                border: '2px solid rgba(34, 197, 94, 0.3)'
              }}>
                <CheckCircle size={28} style={{ color: '#22c55e' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3" style={{ color: '#1e293b' }}>Withdrawal Initiated</h3>
              <p className="text-sm sm:text-base mb-4 sm:mb-6" style={{ color: '#64748b' }}>
                Your bank withdrawal request has been submitted and is being processed. This may take 1-3 business days to complete.
              </p>
            </div>

            <div className="rounded-xl p-3 sm:p-4 border" style={{
              background: 'rgba(0, 0, 0, 0.02)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Amount</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{amount} USDT</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Bank</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>{bankName}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Account</span>
                  <span className="text-right" style={{ color: '#1e293b' }}>••••{accountNumber.slice(-4)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Transaction ID</span>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="font-mono text-xs text-right" style={{ color: '#1e293b' }}>{transactionId}</span>
                    <button
                      onClick={copyTransactionId}
                      className="transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                    >
                      <Copy size={12} className="sm:w-3.5 sm:h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="mr-2" style={{ color: '#64748b' }}>Status</span>
                  <span className="text-right" style={{ color: '#f59e0b' }}>Processing</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center rounded-xl p-3 sm:p-4" style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <span className="text-xs sm:text-sm" style={{ color: '#3b82f6' }}>Track your withdrawal in the Transactions tab</span>
              <button className="transition-colors" style={{ color: '#3b82f6' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
              >
                <ExternalLink size={14} className="sm:w-4 sm:h-4" />
              </button>
            </div>

            <button
              onClick={onClose}
              className="w-full py-2 sm:py-3 rounded-xl font-medium transition-colors text-sm sm:text-base text-white"
              style={{
                background: 'linear-gradient(135deg, #64748b, #475569)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #475569, #334155)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #64748b, #475569)'}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankWithdrawalModal;